import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { playNewTicketSound } from '../services/ticketSound';
import { 
  FiHome, 
  FiPackage, 
  FiTruck, 
  FiShoppingCart,
  FiMenu,
  FiX,
  FiSettings,
  FiTrendingUp,
  FiActivity,
  FiSliders,
  FiFileText,
  FiDatabase,
  FiUser,
  FiUsers,
  FiLogOut,
  FiMapPin,
  FiMail,
  FiCalendar
} from 'react-icons/fi';
import { useSearch } from '../contexts/SearchContext';
import { useAuth } from '../contexts/AuthContext';
import DataLossWarning from './DataLossWarning';
import SearchInputWithHistory from './SearchInputWithHistory';
import { ProductKlasseProvider } from '../contexts/ProductKlasseContext';
import api from '../services/api';
import NavbarSiteMonitor from './NavbarSiteMonitor';
import { warmSlowPages, prefetchAudienceWeek, prefetchAttributieLive } from '../services/pageDataCache';
import './Layout.css';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [afhandelbareOrdersCount, setAfhandelbareOrdersCount] = useState(0);
  const [marketingNewCount, setMarketingNewCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { magazijnSubTab } = useSearch();
  const { user, logout, isAdmin } = useAuth();

  // Hoogste open-ticket-id dat we al gezien hebben. Via een ref i.p.v. state
  // zodat het vergelijken in een interval-callback niet tot stale closures leidt
  // en geen extra re-render triggert wanneer het getal oploopt.
  const lastSeenOpenIdRef = useRef(null);
  const hasInitialStatsRef = useRef(false);

  const refreshTicketBadge = useCallback(async () => {
    try {
      const res = await api.get('/tickets/stats');
      const openCount = typeof res.data?.open === 'number' ? res.data.open : 0;
      const latestOpenId = Number(res.data?.latest_open_id || 0) || 0;
      setOpenTicketCount(openCount);

      // Alleen vanaf de TWEEDE poll vergelijken — bij de eerste poll hebben we
      // nog geen baseline en willen we geen geluid afspelen voor tickets die
      // allang bestonden.
      if (hasInitialStatsRef.current) {
        const prev = lastSeenOpenIdRef.current || 0;
        if (latestOpenId > prev) {
          playNewTicketSound();
        }
      }
      lastSeenOpenIdRef.current = latestOpenId;
      hasInitialStatsRef.current = true;
    } catch {
      setOpenTicketCount(0);
    }
  }, []);

  const refreshMarketingBadge = useCallback(async () => {
    try {
      const res = await api.get('/marketing/stats');
      const n = typeof res.data?.new_proposals === 'number' ? res.data.new_proposals : 0;
      setMarketingNewCount(Math.max(0, n));
    } catch {
      setMarketingNewCount(0);
    }
  }, []);

  const refreshMagazijnBadge = useCallback(async () => {
    try {
      const res = await api.get('/woocommerce/orders-afhandelbaar-stats');
      const n = typeof res.data?.afhandelbaar === 'number' ? res.data.afhandelbaar : 0;
      setAfhandelbareOrdersCount(Math.max(0, n));
    } catch {
      setAfhandelbareOrdersCount(0);
    }
  }, []);

  useEffect(() => {
    refreshTicketBadge();
    const t = setInterval(refreshTicketBadge, 60000);
    const onEvt = () => refreshTicketBadge();
    window.addEventListener('tickets-stats-refresh', onEvt);
    return () => {
      clearInterval(t);
      window.removeEventListener('tickets-stats-refresh', onEvt);
    };
  }, [refreshTicketBadge]);

  // Magazijn-badge: server cachet al 90s, dus een poll elke 2 min is ruim voldoende.
  useEffect(() => {
    refreshMagazijnBadge();
    const t = setInterval(refreshMagazijnBadge, 2 * 60 * 1000);
    const onEvt = () => refreshMagazijnBadge();
    window.addEventListener('magazijn-orders-refresh', onEvt);
    return () => {
      clearInterval(t);
      window.removeEventListener('magazijn-orders-refresh', onEvt);
    };
  }, [refreshMagazijnBadge]);

  useEffect(() => {
    refreshMarketingBadge();
    const t = setInterval(refreshMarketingBadge, 5 * 60 * 1000);
    const onEvt = () => refreshMarketingBadge();
    window.addEventListener('marketing-stats-refresh', onEvt);
    return () => {
      clearInterval(t);
      window.removeEventListener('marketing-stats-refresh', onEvt);
    };
  }, [refreshMarketingBadge]);

  // Klanten 360° + live bezoekers vooraf laden (idle), zodat navigatie sneller voelt
  useEffect(() => {
    warmSlowPages({ days: 7, liveMinutes: 5 });
    const t = setInterval(() => warmSlowPages({ days: 7, liveMinutes: 5 }), 2 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const navbarSearchPlaceholder = (() => {
    if (location.pathname === '/producten') return 'Zoek op EAN of product naam...';
    if (location.pathname === '/leveranciers') return 'Zoek op leverancier naam...';
    if (location.pathname === '/inkooporders') return 'Zoek op ordernummer of leverancier...';
    if (location.pathname === '/orders-onderweg') return 'Zoek op ordernummer of EAN...';
    if (location.pathname === '/inkoopvoorstellen') return 'Zoek op product of leverancier...';
    if (location.pathname === '/magazijn') {
      if (magazijnSubTab === 'retouren') return 'Zoek retour (nr, order, e-mail, postcode 4 cijfers…)';
      return 'Zoek op EAN, SKU of productnaam...';
    }
    return 'Zoeken...';
  })();

  const menuItems = [
    { path: '/', icon: FiHome, label: 'Dashboard' },
    { path: '/producten', icon: FiPackage, label: 'Producten' },
    { path: '/magazijn', icon: FiMapPin, label: 'Magazijn' },
    { path: '/leveranciers', icon: FiTruck, label: 'Leveranciers' },
    { path: '/inkoopvoorstellen', icon: FiFileText, label: 'Inkoopvoorstellen' },
    { path: '/marketing', icon: FiCalendar, label: 'Marketing' },
    { path: '/klanten', icon: FiUsers, label: 'Klanten' },
    { path: '/verkoopdata', icon: FiTrendingUp, label: 'Verkoopdata' },
    { path: '/attributie', icon: FiActivity, label: 'Attributie' },
    { path: '/koppelingen', icon: FiSettings, label: 'Koppelingen' },
    { path: '/tickets', icon: FiMail, label: 'Tickets' },
    { path: '/parameters', icon: FiSliders, label: 'Parameters' },
    { path: '/migratie', icon: FiDatabase, label: 'Migratie' },
  ];

  // Add admin menu items
  if (isAdmin()) {
    menuItems.push({ path: '/gebruikers', icon: FiUser, label: 'Gebruikers' });
  }

  return (
    <DataLossWarning>
    <div className="layout">
      <nav className="navbar">
        <div className="navbar-start">
          <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
          <Link to="/" className="navbar-brand" title="Naar dashboard">
            <img src="/logo.png" alt="Logo" className="navbar-logo" onError={(e) => {
              e.target.style.display = 'none';
            }} />
            <h1 className="navbar-title">4SHINE Inkoop Software</h1>
          </Link>
        </div>
        <div className="navbar-center">
          <NavbarSiteMonitor />
        </div>
        <div className="navbar-end">
          <form className="navbar-search" onSubmit={handleSearch}>
            <SearchInputWithHistory variant="navbar" placeholder={navbarSearchPlaceholder} />
          </form>
          <div className="navbar-user">
            <span className="user-info">
              <FiUser className="user-icon" />
              {user?.gebruikersnaam}
              {isAdmin() && <span className="user-role">(Admin)</span>}
            </span>
            <button className="logout-btn" onClick={handleLogout} title="Uitloggen">
              <FiLogOut />
            </button>
          </div>
        </div>
      </nav>

      <div className="layout-container">
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <nav className="sidebar-nav">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isTickets = item.path === '/tickets';
              const isMagazijn = item.path === '/magazijn';
              const isMarketing = item.path === '/marketing';
              const badgeCount = isTickets
                ? openTicketCount
                : isMagazijn
                  ? afhandelbareOrdersCount
                  : isMarketing
                    ? marketingNewCount
                    : 0;
              const badgeTitle = isTickets
                ? 'Open tickets'
                : isMagazijn
                  ? 'Orders nu pickbaar (voorraad OK, niet uitgesteld)'
                  : isMarketing
                    ? 'Nieuwe marketing-voorstellen'
                    : '';
              const showBadge = (isTickets || isMagazijn || isMarketing) && badgeCount > 0;
              const prefetchNav =
                item.path === '/klanten'
                  ? () => prefetchAudienceWeek(7).catch(() => {})
                  : item.path === '/attributie'
                    ? () => prefetchAttributieLive(5).catch(() => {})
                    : undefined;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  onMouseEnter={prefetchNav}
                  onFocus={prefetchNav}
                >
                  <Icon className="nav-icon" />
                  <span>{item.label}</span>
                  {showBadge ? (
                    <span className="nav-badge" title={badgeTitle}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="main-content">
          <ProductKlasseProvider>
            {children}
          </ProductKlasseProvider>
        </main>
      </div>
    </div>
    </DataLossWarning>
  );
};

export default Layout;
